import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Building2, Users, KeyRound, Plus, Trash2, Power, PowerOff,
  Eye, EyeOff, RotateCcw, Search, Shield, Edit2, Check, X, ChevronDown
} from "lucide-react";

// ===================== TAB: ORGANIZATIONS =====================
function OrganizationsTab() {
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "" });
  const [creating, setCreating] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [search, setSearch] = useState("");

  const fetchOrgs = useCallback(async () => {
    try {
      const { data } = await api.get("/super-admin/organizations");
      setOrgs(data);
    } catch { toast.error("Errore caricamento organizzazioni"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchOrgs(); }, [fetchOrgs]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return;
    setCreating(true);
    try {
      await api.post("/super-admin/organizations", form);
      toast.success("Organizzazione creata");
      setShowCreate(false);
      setForm({ name: "", email: "", phone: "", address: "" });
      fetchOrgs();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setCreating(false); }
  };

  const handleRemove = async (org, mode) => {
    try {
      await api.post(`/super-admin/organizations/${org.id}/remove`, { mode });
      toast.success(mode === "delete" ? "Organizzazione eliminata" : "Organizzazione disattivata");
      setConfirmDelete(null);
      fetchOrgs();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const handleReactivate = async (org) => {
    try {
      await api.post(`/super-admin/organizations/${org.id}/reactivate`);
      toast.success("Organizzazione riattivata");
      fetchOrgs();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const filtered = orgs.filter(o => o.name?.toLowerCase().includes(search.toLowerCase()));

  if (loading) return <div className="py-8 text-center text-zinc-400">Caricamento...</div>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
          <Input placeholder="Cerca organizzazione..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-sm" data-testid="sa-org-search" />
        </div>
        <Button onClick={() => setShowCreate(!showCreate)} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="sa-create-org-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuova Azienda
        </Button>
      </div>

      {showCreate && (
        <form onSubmit={handleCreate} className="bg-white border border-zinc-200 rounded-sm p-4 space-y-3" data-testid="sa-create-org-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Nome Azienda *</Label>
              <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Es: Traslochi Milano SRL" required data-testid="sa-org-name" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Email</Label>
              <Input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="info@azienda.it" data-testid="sa-org-email" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Telefono</Label>
              <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+39..." data-testid="sa-org-phone" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Indirizzo</Label>
              <Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Via..." data-testid="sa-org-address" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={() => setShowCreate(false)} className="rounded-sm">Annulla</Button>
            <Button type="submit" disabled={creating} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="sa-org-submit">{creating ? "Creazione..." : "Crea Azienda"}</Button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {filtered.map(org => (
          <div key={org.id} className={`bg-white border rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${!org.is_active ? 'opacity-60 border-red-200 bg-red-50/30' : 'border-zinc-200'}`} data-testid={`sa-org-${org.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <Building2 className="w-4 h-4 text-zinc-400 shrink-0" />
                <span className="font-medium truncate">{org.name}</span>
                {!org.is_active && <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded-full">Disattivata</span>}
              </div>
              <div className="text-xs text-zinc-400 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                {org.email && <span>{org.email}</span>}
                <span>{org.user_count} utent{org.user_count === 1 ? 'e' : 'i'}</span>
                <span>Creata: {new Date(org.created_at).toLocaleDateString('it-IT')}</span>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              {!org.is_active ? (
                <Button size="sm" variant="outline" onClick={() => handleReactivate(org)} className="rounded-sm text-green-600 border-green-200 hover:bg-green-50" data-testid={`sa-org-reactivate-${org.id}`}>
                  <Power className="w-3.5 h-3.5 mr-1" /> Riattiva
                </Button>
              ) : (
                <>
                  {confirmDelete?.id === org.id ? (
                    <div className="flex items-center gap-1 bg-red-50 border border-red-200 rounded-sm px-2 py-1">
                      <span className="text-xs text-red-600 mr-1">Azione:</span>
                      <Button size="sm" variant="ghost" onClick={() => handleRemove(org, "deactivate")} className="h-7 px-2 text-xs text-amber-600 hover:bg-amber-50" data-testid={`sa-org-deactivate-${org.id}`}>
                        <PowerOff className="w-3 h-3 mr-1" /> Disattiva
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleRemove(org, "delete")} className="h-7 px-2 text-xs text-red-600 hover:bg-red-100" data-testid={`sa-org-delete-${org.id}`}>
                        <Trash2 className="w-3 h-3 mr-1" /> Elimina
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(null)} className="h-7 px-1"><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => setConfirmDelete(org)} className="rounded-sm text-red-500 border-red-200 hover:bg-red-50" data-testid={`sa-org-remove-${org.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Rimuovi
                    </Button>
                  )}
                </>
              )}
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center text-zinc-400 py-8">Nessuna organizzazione trovata</p>}
      </div>
    </div>
  );
}

// ===================== TAB: USERS =====================
function UsersTab() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [resetTarget, setResetTarget] = useState(null);
  const [newPassword, setNewPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchUsers = useCallback(async () => {
    try {
      const { data } = await api.get("/super-admin/users");
      setUsers(data);
    } catch { toast.error("Errore caricamento utenti"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error("Min. 6 caratteri"); return; }
    setResetting(true);
    try {
      await api.put(`/super-admin/users/${resetTarget.id}/password`, { new_password: newPassword });
      toast.success(`Password aggiornata per ${resetTarget.email}`);
      setResetTarget(null);
      setNewPassword("");
      setShowPw(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setResetting(false); }
  };

  const handleToggleActive = async (u) => {
    try {
      const { data } = await api.put(`/super-admin/users/${u.id}/toggle-active`);
      toast.success(data.message);
      fetchUsers();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const filtered = users.filter(u =>
    !u.is_super_admin &&
    (u.email?.toLowerCase().includes(search.toLowerCase()) ||
     u.name?.toLowerCase().includes(search.toLowerCase()) ||
     u.organization_name?.toLowerCase().includes(search.toLowerCase()))
  );

  if (loading) return <div className="py-8 text-center text-zinc-400">Caricamento...</div>;

  return (
    <div className="space-y-4">
      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
        <Input placeholder="Cerca utente o azienda..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 rounded-sm" data-testid="sa-user-search" />
      </div>

      {/* Password reset modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={() => { setResetTarget(null); setNewPassword(""); setShowPw(false); }}>
          <div className="bg-white rounded-sm shadow-xl p-6 w-full max-w-sm mx-4 space-y-4" onClick={e => e.stopPropagation()} data-testid="sa-reset-pw-modal">
            <h3 className="font-semibold">Reset Password</h3>
            <p className="text-sm text-zinc-500">Imposta una nuova password per <strong>{resetTarget.email}</strong></p>
            <div className="relative">
              <Input
                type={showPw ? "text" : "password"}
                value={newPassword}
                onChange={e => setNewPassword(e.target.value)}
                placeholder="Nuova password (min. 6 caratteri)"
                className="pr-10 rounded-sm"
                data-testid="sa-new-password-input"
              />
              <button type="button" onClick={() => setShowPw(v => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600" tabIndex={-1}>
                {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setResetTarget(null); setNewPassword(""); setShowPw(false); }} className="rounded-sm">Annulla</Button>
              <Button onClick={handleResetPassword} disabled={resetting} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="sa-confirm-reset-pw">
                {resetting ? "Salvataggio..." : "Salva Password"}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {filtered.map(u => (
          <div key={u.id} className={`bg-white border rounded-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${!u.is_active ? 'opacity-60 border-red-200 bg-red-50/30' : 'border-zinc-200'}`} data-testid={`sa-user-${u.id}`}>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-[#002FA7] flex items-center justify-center text-white text-xs font-medium shrink-0">
                  {u.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div className="min-w-0">
                  <span className="font-medium text-sm truncate block">{u.name || u.email}</span>
                  <span className="text-xs text-zinc-400">{u.email}</span>
                </div>
              </div>
              <div className="text-xs text-zinc-400 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                <span className="font-medium text-zinc-500">{u.organization_name}</span>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${u.role === 'OWNER' ? 'bg-blue-50 text-blue-600' : 'bg-zinc-100 text-zinc-600'}`}>{u.role}</span>
                {!u.is_active && <span className="px-1.5 py-0.5 rounded bg-red-100 text-red-600 text-[10px] font-medium">Disattivato</span>}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button size="sm" variant="outline" onClick={() => { setResetTarget(u); setNewPassword(""); setShowPw(false); }} className="rounded-sm" data-testid={`sa-user-reset-${u.id}`}>
                <RotateCcw className="w-3.5 h-3.5 mr-1" /> Password
              </Button>
              <Button size="sm" variant="outline" onClick={() => handleToggleActive(u)}
                className={`rounded-sm ${u.is_active ? 'text-amber-600 border-amber-200 hover:bg-amber-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}
                data-testid={`sa-user-toggle-${u.id}`}>
                {u.is_active ? <><PowerOff className="w-3.5 h-3.5 mr-1" /> Disattiva</> : <><Power className="w-3.5 h-3.5 mr-1" /> Attiva</>}
              </Button>
            </div>
          </div>
        ))}
        {filtered.length === 0 && <p className="text-center text-zinc-400 py-8">Nessun utente trovato</p>}
      </div>
    </div>
  );
}

// ===================== TAB: REGISTRATION CODES =====================
function CodesTab() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ code: "", label: "", max_uses: "", expires_at: "" });
  const [saving, setSaving] = useState(false);
  const [showCode, setShowCode] = useState({});

  const fetchCodes = useCallback(async () => {
    try {
      const { data } = await api.get("/super-admin/registration-codes");
      setCodes(data);
    } catch { toast.error("Errore caricamento codici"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchCodes(); }, [fetchCodes]);

  const resetForm = () => { setForm({ code: "", label: "", max_uses: "", expires_at: "" }); setShowCreate(false); setEditingId(null); };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.code.trim()) return;
    setSaving(true);
    const payload = {
      code: form.code,
      label: form.label,
      max_uses: form.max_uses ? parseInt(form.max_uses) : null,
      expires_at: form.expires_at || null,
    };
    try {
      if (editingId) {
        await api.put(`/super-admin/registration-codes/${editingId}`, payload);
        toast.success("Codice aggiornato");
      } else {
        await api.post("/super-admin/registration-codes", payload);
        toast.success("Codice creato");
      }
      resetForm();
      fetchCodes();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
    finally { setSaving(false); }
  };

  const handleEdit = (code) => {
    setEditingId(code.id);
    setForm({
      code: code.code,
      label: code.label || "",
      max_uses: code.max_uses != null ? String(code.max_uses) : "",
      expires_at: code.expires_at ? code.expires_at.slice(0, 16) : "",
    });
    setShowCreate(true);
  };

  const handleDelete = async (codeId) => {
    try {
      await api.delete(`/super-admin/registration-codes/${codeId}`);
      toast.success("Codice eliminato");
      fetchCodes();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  const handleToggle = async (codeId) => {
    try {
      await api.put(`/super-admin/registration-codes/${codeId}/toggle`);
      fetchCodes();
    } catch (err) { toast.error(err.response?.data?.detail || "Errore"); }
  };

  if (loading) return <div className="py-8 text-center text-zinc-400">Caricamento...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-zinc-500">{codes.length} codic{codes.length === 1 ? 'e' : 'i'} di registrazione</p>
        <Button onClick={() => { resetForm(); setShowCreate(true); }} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="sa-create-code-btn">
          <Plus className="w-4 h-4 mr-1" /> Nuovo Codice
        </Button>
      </div>

      {showCreate && (
        <form onSubmit={handleSave} className="bg-white border border-zinc-200 rounded-sm p-4 space-y-3" data-testid="sa-code-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Codice *</Label>
              <Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="Es: REG-AZIENDA-2026" required data-testid="sa-code-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Etichetta</Label>
              <Input value={form.label} onChange={e => setForm({ ...form, label: e.target.value })} placeholder="Es: Per Traslochi Milano" data-testid="sa-code-label" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Utilizzi Massimi</Label>
              <Input type="number" min="1" value={form.max_uses} onChange={e => setForm({ ...form, max_uses: e.target.value })} placeholder="Illimitato" data-testid="sa-code-max-uses" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs font-semibold uppercase tracking-wider text-zinc-500">Scadenza</Label>
              <Input type="datetime-local" value={form.expires_at} onChange={e => setForm({ ...form, expires_at: e.target.value })} data-testid="sa-code-expires" />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button type="button" variant="outline" onClick={resetForm} className="rounded-sm">Annulla</Button>
            <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002482] rounded-sm" data-testid="sa-code-submit">
              {saving ? "Salvataggio..." : editingId ? "Aggiorna" : "Crea Codice"}
            </Button>
          </div>
        </form>
      )}

      <div className="space-y-2">
        {codes.map(code => (
          <div key={code.id} className={`bg-white border rounded-sm p-4 ${!code.is_active ? 'opacity-60 border-red-200 bg-red-50/30' : 'border-zinc-200'}`} data-testid={`sa-code-${code.id}`}>
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-zinc-400 shrink-0" />
                  <span className="font-mono text-sm font-medium">
                    {showCode[code.id] ? code.code : '••••••••••••'}
                  </span>
                  <button type="button" onClick={() => setShowCode(s => ({ ...s, [code.id]: !s[code.id] }))} className="text-zinc-400 hover:text-zinc-600" data-testid={`sa-code-toggle-vis-${code.id}`}>
                    {showCode[code.id] ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                  </button>
                  {!code.is_active && <span className="text-xs px-2 py-0.5 bg-red-100 text-red-600 rounded-full">Disattivato</span>}
                </div>
                <div className="text-xs text-zinc-400 mt-1 flex flex-wrap gap-x-4 gap-y-1">
                  {code.label && <span className="text-zinc-500">{code.label}</span>}
                  <span>Utilizzi: {code.used_count}{code.max_uses != null ? `/${code.max_uses}` : ' (illimitato)'}</span>
                  {code.expires_at && <span>Scade: {new Date(code.expires_at).toLocaleDateString('it-IT')}</span>}
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => handleEdit(code)} className="rounded-sm" data-testid={`sa-code-edit-${code.id}`}>
                  <Edit2 className="w-3.5 h-3.5 mr-1" /> Modifica
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleToggle(code.id)}
                  className={`rounded-sm ${code.is_active ? 'text-amber-600 border-amber-200 hover:bg-amber-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}
                  data-testid={`sa-code-toggle-${code.id}`}>
                  {code.is_active ? <PowerOff className="w-3.5 h-3.5" /> : <Power className="w-3.5 h-3.5" />}
                </Button>
                <Button size="sm" variant="outline" onClick={() => handleDelete(code.id)} className="rounded-sm text-red-500 border-red-200 hover:bg-red-50" data-testid={`sa-code-delete-${code.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          </div>
        ))}
        {codes.length === 0 && <p className="text-center text-zinc-400 py-8">Nessun codice di registrazione</p>}
      </div>
    </div>
  );
}

// ===================== MAIN PAGE =====================
const TABS = [
  { key: "orgs", label: "Aziende", icon: Building2 },
  { key: "users", label: "Utenti", icon: Users },
  { key: "codes", label: "Codici Registrazione", icon: KeyRound },
];

export default function SuperAdmin() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("orgs");

  if (!user?.is_super_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="super-admin-page">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Shield className="w-5 h-5 text-[#002FA7]" />
          <h1 className="text-2xl font-bold tracking-tight font-['Outfit']">Pannello Super Admin</h1>
        </div>
        <p className="text-sm text-zinc-500">Gestisci aziende, utenti e codici di registrazione</p>
      </div>

      {/* Tab Navigation */}
      <div className="flex gap-1 bg-zinc-100 p-1 rounded-sm w-fit" data-testid="sa-tabs">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            data-testid={`sa-tab-${tab.key}`}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-sm text-sm font-medium transition-colors ${
              activeTab === tab.key
                ? 'bg-white text-[#002FA7] shadow-sm'
                : 'text-zinc-500 hover:text-zinc-700'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "orgs" && <OrganizationsTab />}
      {activeTab === "users" && <UsersTab />}
      {activeTab === "codes" && <CodesTab />}
    </div>
  );
}
